/**
 * useApiResource — generic data-fetching hook für typed API-Calls.
 *
 * Liefert {data, loading, error, refetch}. Lifecycle-bewusst (kein
 * setState nach Unmount). Re-fetcht wenn der `key` sich ändert.
 *
 * Beispiel:
 *   const { data, loading, error, refetch } = useApiResource(
 *     ['staff-tokens', filter],
 *     () => staffApi.tokens.list({ status: filter })
 *   )
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { ApiError } from '../api/client'

export interface UseApiResourceResult<T> {
  data: T | null
  loading: boolean
  error: ApiError | Error | null
  refetch: () => Promise<void>
  /** Setzen ohne Re-Fetch — z.B. nach optimistic update */
  setData: (next: T | null) => void
}

export function useApiResource<T>(
  key: ReadonlyArray<unknown>,
  fetcher: () => Promise<T>,
  options: { enabled?: boolean } = {}
): UseApiResourceResult<T> {
  const enabled = options.enabled ?? true
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState<boolean>(enabled)
  const [error, setError] = useState<ApiError | Error | null>(null)

  // capture latest fetcher in ref so we can call it from refetch without
  // depending on the function identity (otherwise refetch would reset every
  // render where the caller passes an inline arrow function)
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  // re-render trigger via stable serialised key
  const keyStr = JSON.stringify(key)

  const run = useCallback(async (signal: AbortSignal | null) => {
    setLoading(true)
    setError(null)
    try {
      const result = await fetcherRef.current()
      if (signal?.aborted) return
      setData(result)
    } catch (e) {
      if (signal?.aborted) return
      setError(e as ApiError | Error)
    } finally {
      if (!signal?.aborted) setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!enabled) {
      setLoading(false)
      return
    }
    const ac = new AbortController()
    void run(ac.signal)
    return () => ac.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyStr, enabled, run])

  const refetch = useCallback(() => run(null), [run])

  return { data, loading, error, refetch, setData }
}
